import React, { useEffect, useRef } from 'react';
import { BrowserMultiFormatReader } from '@zxing/library';

// The rear camera through zxing, which reads product barcodes (EAN) and batch
// QRs alike. Calls onScan with every code it reads (zxing waits 500 ms between
// reads) and onError if the camera can't be opened. The camera is off once
// this unmounts, even if it was still being opened.
function BarcodeCamera({ onScan, onError }) {
  const video = useRef(null);
  const handlers = useRef({ onScan, onError });

  useEffect(() => {
    handlers.current = { onScan, onError };
  });

  useEffect(() => {
    const reader = new BrowserMultiFormatReader();
    let stream = null;
    let stopped = false;

    const open = async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('camera API unavailable');
      }
      const opened = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      if (stopped) {
        opened.getTracks().forEach((track) => track.stop());
        return;
      }
      stream = opened;
      // Frames without a code come as (null, error); only reads matter.
      await reader.decodeFromStream(opened, video.current, (result) => {
        if (result) handlers.current.onScan(result.getText());
      });
    };

    open().catch((err) => {
      if (!stopped) handlers.current.onError(err);
    });

    return () => {
      stopped = true;
      reader.reset();
      if (stream) stream.getTracks().forEach((track) => track.stop());
    };
  }, []);

  return <video ref={video} autoPlay playsInline muted />;
}

export default BarcodeCamera;
